import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';

interface Tenant {
  id: string;
  companyName: string;
  tenantId: string;
  region: string;
  plan: string;
  status: 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled';
  createdAt: string;
  usersCount: number;
  frameworksCount: number;
  storageUsedGb: number;
}

const REGION_OPTIONS = [
  { value: '', label: 'All regions' },
  { value: 'ksa', label: 'KSA' },
  { value: 'eu', label: 'EU' },
  { value: 'us', label: 'US' },
];

const PLAN_OPTIONS = [
  { value: '', label: 'All plans' },
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'trial', label: 'Trial' },
  { value: 'active', label: 'Active' },
  { value: 'past_due', label: 'Past Due' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function TenantManagement() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterRegion, setFilterRegion] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionTenant, setActionTenant] = useState<Tenant | null>(null);
  const [acting, setActing] = useState(false);

  const isAdmin = !!user?.isPlatformAdmin;

  useEffect(() => {
    if (!isAdmin) return;
    fetchTenants();
  }, [isAdmin]);

  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      if (filterRegion && t.region !== filterRegion) return false;
      if (filterPlan && t.plan !== filterPlan) return false;
      if (filterStatus && t.status !== filterStatus) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !t.companyName.toLowerCase().includes(q) &&
          !t.tenantId.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [tenants, filterRegion, filterPlan, filterStatus, search]);

  async function fetchTenants() {
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<Tenant[]>(
      '/api/admin/tenants'
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setTenants(data);
    }
    setLoading(false);
  }

  async function handleSuspend(tenant: Tenant) {
    setActing(true);

    const { error: apiError } = await apiClient.patch(
      `/api/admin/tenants/${tenant.id}/suspend`
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setTenants((prev) =>
        prev.map((t) =>
          t.id === tenant.id ? { ...t, status: 'suspended' as const } : t
        )
      );
    }
    setActing(false);
    setShowSuspendConfirm(false);
    setActionTenant(null);
  }

  async function handleDelete(tenant: Tenant) {
    setActing(true);

    const { error: apiError } = await apiClient.delete(
      `/api/admin/tenants/${tenant.id}`
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setTenants((prev) => prev.filter((t) => t.id !== tenant.id));
    }
    setActing(false);
    setShowDeleteConfirm(false);
    setActionTenant(null);
  }

  const statusBadgeVariant = (status: Tenant['status']) => {
    switch (status) {
      case 'trial':
        return 'brand' as const;
      case 'active':
        return 'success' as const;
      case 'past_due':
        return 'warning' as const;
      case 'suspended':
        return 'danger' as const;
      case 'cancelled':
        return 'default' as const;
      default:
        return 'default' as const;
    }
  };

  const regionBadgeVariant = (region: string) => {
    switch (region) {
      case 'ksa':
        return 'brand' as const;
      case 'eu':
        return 'info' as const;
      case 'us':
        return 'success' as const;
      default:
        return 'default' as const;
    }
  };

  const planBadgeVariant = (plan: string) => {
    switch (plan) {
      case 'free':
        return 'default' as const;
      case 'starter':
        return 'info' as const;
      case 'pro':
        return 'brand' as const;
      case 'enterprise':
        return 'warning' as const;
      default:
        return 'default' as const;
    }
  };

  function formatDate(dateStr: string) {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
          </div>
          <h1 className="text-2xl font-display font-bold text-white mb-2">
            Access Denied
          </h1>
          <p className="text-slate-400">
            You do not have permission to view this page. Platform admin access is required.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-white">
          Tenant Management
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          {tenants.length} total tenant{tenants.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-surface-mid rounded-xl border border-border p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input
            placeholder="Search by company or ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            options={REGION_OPTIONS}
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
          />
          <Select
            options={PLAN_OPTIONS}
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
          />
          <Select
            options={STATUS_OPTIONS}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          />
        </div>
      </div>

      {/* Tenant table */}
      {filteredTenants.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">
              {tenants.length === 0
                ? 'No tenants found.'
                : 'No tenants match your filters.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="bg-surface-mid rounded-xl border border-border overflow-hidden">
          {/* Table header */}
          <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-xs text-slate-500 uppercase tracking-wider">
            <div className="col-span-3">Company</div>
            <div className="col-span-2">Tenant ID</div>
            <div className="col-span-1">Region</div>
            <div className="col-span-1">Plan</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2">Created</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Table rows */}
          {filteredTenants.map((tenant) => (
            <div
              key={tenant.id}
              className="grid grid-cols-1 lg:grid-cols-12 gap-2 lg:gap-4 px-6 py-4 border-b border-border last:border-b-0 items-center hover:bg-surface-light/20 cursor-pointer transition-colors"
              onClick={() => {
                setSelectedTenant(tenant);
                setShowDetail(true);
              }}
            >
              <div className="lg:col-span-3">
                <p className="text-sm font-medium text-white truncate">
                  {tenant.companyName}
                </p>
              </div>
              <div className="lg:col-span-2">
                <p className="text-sm text-slate-400 truncate font-mono text-xs">
                  {tenant.tenantId}
                </p>
              </div>
              <div className="lg:col-span-1">
                <Badge variant={regionBadgeVariant(tenant.region)}>
                  {tenant.region.toUpperCase()}
                </Badge>
              </div>
              <div className="lg:col-span-1">
                <Badge variant={planBadgeVariant(tenant.plan)}>
                  {tenant.plan}
                </Badge>
              </div>
              <div className="lg:col-span-1">
                <Badge variant={statusBadgeVariant(tenant.status)}>
                  {tenant.status}
                </Badge>
              </div>
              <div className="lg:col-span-2">
                <p className="text-sm text-slate-500">
                  {formatDate(tenant.createdAt)}
                </p>
              </div>
              <div className="lg:col-span-2 flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTenant(tenant);
                    setShowDetail(true);
                  }}
                >
                  View
                </Button>
                {tenant.status !== 'suspended' && tenant.status !== 'cancelled' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActionTenant(tenant);
                      setShowSuspendConfirm(true);
                    }}
                    className="text-yellow-400 hover:text-yellow-300"
                  >
                    Suspend
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActionTenant(tenant);
                    setShowDeleteConfirm(true);
                  }}
                  className="text-red-400 hover:text-red-300"
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <Modal
        isOpen={showDetail}
        onClose={() => {
          setShowDetail(false);
          setSelectedTenant(null);
        }}
        title="Tenant Details"
        size="lg"
        footer={
          <Button
            variant="secondary"
            onClick={() => {
              setShowDetail(false);
              setSelectedTenant(null);
            }}
          >
            Close
          </Button>
        }
      >
        {selectedTenant && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                  Company
                </p>
                <p className="text-sm text-white">{selectedTenant.companyName}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                  Tenant ID
                </p>
                <p className="text-sm text-white font-mono text-xs">
                  {selectedTenant.tenantId}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                  Region
                </p>
                <Badge variant={regionBadgeVariant(selectedTenant.region)}>
                  {selectedTenant.region.toUpperCase()}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                  Plan
                </p>
                <Badge variant={planBadgeVariant(selectedTenant.plan)}>
                  {selectedTenant.plan}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                  Status
                </p>
                <Badge variant={statusBadgeVariant(selectedTenant.status)}>
                  {selectedTenant.status}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                  Created
                </p>
                <p className="text-sm text-white">
                  {formatDate(selectedTenant.createdAt)}
                </p>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">
                Usage Statistics
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-surface-dark rounded-lg p-3 border border-border">
                  <p className="text-xs text-slate-500 mb-1">Users</p>
                  <p className="text-lg font-display font-bold text-white">
                    {selectedTenant.usersCount}
                  </p>
                </div>
                <div className="bg-surface-dark rounded-lg p-3 border border-border">
                  <p className="text-xs text-slate-500 mb-1">Frameworks</p>
                  <p className="text-lg font-display font-bold text-white">
                    {selectedTenant.frameworksCount}
                  </p>
                </div>
                <div className="bg-surface-dark rounded-lg p-3 border border-border">
                  <p className="text-xs text-slate-500 mb-1">Storage</p>
                  <p className="text-lg font-display font-bold text-white">
                    {selectedTenant.storageUsedGb} GB
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Suspend confirmation */}
      <Modal
        isOpen={showSuspendConfirm}
        onClose={() => {
          setShowSuspendConfirm(false);
          setActionTenant(null);
        }}
        title="Suspend Tenant"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSuspendConfirm(false);
                setActionTenant(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={acting}
              onClick={() => actionTenant && handleSuspend(actionTenant)}
            >
              Suspend
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Are you sure you want to suspend{' '}
          <span className="font-semibold text-white">
            {actionTenant?.companyName}
          </span>
          ? This will disable access for all users in this workspace.
        </p>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setActionTenant(null);
        }}
        title="Delete Tenant"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteConfirm(false);
                setActionTenant(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={acting}
              onClick={() => actionTenant && handleDelete(actionTenant)}
            >
              Delete Permanently
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Are you sure you want to permanently delete{' '}
          <span className="font-semibold text-white">
            {actionTenant?.companyName}
          </span>
          ? This action cannot be undone and all data will be lost.
        </p>
      </Modal>
    </div>
  );
}
